/* Konfigurator pakietów BrainJell.

   Zasady:
   - Bez JS formularz nadal działa: to zwykły POST na /cart/add, radio i
     pole ilości są prawdziwymi polami. JS tylko podmienia to na AJAX.
   - Co po dodaniu: szuflada koszyka Dawn albo przejście prosto do kasy —
     decyduje data-checkout-direct, sterowane ustawieniem sekcji.
   - Przy settings.bj_single_item_cart sekcja dokłada data-single-item:
     ilość jest wtedy zawsze 1, a koszyk czyścimy przed dodaniem, żeby
     wybór innego pakietu podmieniał zawartość, a nie się dokładał.
   - Ceny subskrypcji NIE są liczone tutaj. Biorą się z
     selling_plan_allocations wyrenderowanych w Liquidzie, bo tylko one
     zgadzają się z tym, co naliczy checkout. */

/* Odpowiedniki placeholderów z shop.money_format:
   [liczba miejsc po przecinku, separator tysięcy, separator dziesiętny]. */
const BJ_MONEY_RULES = {
  amount: [2, ',', '.'],
  amount_no_decimals: [0, ',', '.'],
  amount_with_comma_separator: [2, '.', ','],
  amount_no_decimals_with_comma_separator: [0, '.', ','],
  amount_with_apostrophe_separator: [2, "'", '.'],
  amount_with_space_separator: [2, ' ', ','],
  amount_no_decimals_with_space_separator: [0, ' ', ','],
};

class BjBuyBox extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-bj-form]');
    if (!this.form) return;

    this.packages = Array.from(this.querySelectorAll('[data-bj-package]'));
    this.variantInput = this.querySelector('[data-bj-variant-id]');
    this.variantMirror = this.querySelector('[data-bj-variant-id-mirror]');
    this.planInput = this.querySelector('[data-bj-selling-plan]');
    this.subToggle = this.querySelector('[data-bj-sub-toggle]');
    this.priceEl = this.querySelector('[data-bj-price]');
    this.compareEl = this.querySelector('[data-bj-compare]');
    this.qtyInput = this.querySelector('input[name="quantity"]');
    this.submit = this.querySelector('[data-bj-submit]');
    this.errorEl = this.querySelector('[data-bj-error]');
    this.checkoutDirect = this.hasAttribute('data-checkout-direct');

    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('submit', this.onSubmit);

    this.querySelector('[data-bj-qty-down]')?.addEventListener('click', () => this.stepQty(-1));
    this.querySelector('[data-bj-qty-up]')?.addEventListener('click', () => this.stepQty(1));
    this.subToggle?.addEventListener('click', () => this.toggleSubscription());

    this.setupAnchorScroll();
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onAnchorClick);
  }

  /* Wszystkie „Zamów" (nagłówek, pasek darmowej dostawy, pływający przycisk,
     CTA w sekcjach) prowadzą do kotwicy sekcji, czyli do jej górnej krawędzi.
     Na desktopie to dobre miejsce — panel stoi obok zdjęcia. Poniżej 1024px
     (próg z brainjell.css, gdzie hero-grid przechodzi w jedną kolumnę) panel
     ląduje pod zdjęciem, a sekcja zaczyna się tuż pod nagłówkiem: przewinięcie
     do niej to kilkadziesiąt pikseli i wygląda jak błąd. Tam przewijamy więc
     prosto do wyboru pakietu. Adres dostaje tę samą kotwicę co bez JS. */
  setupAnchorScroll() {
    this.scrollTarget = this.querySelector('[data-bj-scroll-target]');
    this.anchorId = this.closest('section[id]')?.id;
    if (!this.scrollTarget || !this.anchorId) return;

    this.stacked = window.matchMedia('(max-width: 1024px)');
    this.onAnchorClick = this.onAnchorClick.bind(this);
    document.addEventListener('click', this.onAnchorClick);

    // Wejście z gotową kotwicą (/#produkt z reklamy albo z podstrony) —
    // przeglądarka zatrzymała się już na górze sekcji, poprawiamy na pakiety.
    if (location.hash === '#' + this.anchorId && this.stacked.matches) {
      requestAnimationFrame(() => this.scrollToPicker());
    }
  }

  onAnchorClick(event) {
    // Pływający przycisk z pełnym koszykiem sam obsługuje kliknięcie (szuflada).
    if (event.defaultPrevented || !this.stacked.matches) return;

    const link = event.target.closest('a[href*="#"]');
    if (!link || link.hash !== '#' + this.anchorId) return;
    if (link.origin !== location.origin || link.pathname !== location.pathname) return;

    event.preventDefault();
    if (location.hash !== link.hash) history.pushState(null, '', link.hash);
    this.scrollToPicker();
  }

  // Odstęp od sticky nagłówka daje scroll-padding-top z brainjell.css,
  // a płynność — scroll-behavior (wyłączone przy prefers-reduced-motion).
  scrollToPicker() {
    this.scrollTarget.scrollIntoView({ block: 'start' });
  }

  /* Format bierzemy z ustawień sklepu (shop.money_format), nie z Intl.
     Intl potrzebowałby locale, a request.locale.iso_code to język motywu —
     przy angielskim interfejsie dawał "PLN 327.00" zamiast "327,00 zł".
     Ten sam format co filtr `money` = kwota z JS-a i z Liquida wyglądają
     identycznie. */
  money(cents) {
    const format = this.dataset.moneyFormat || '{{amount_with_comma_separator}} zł';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) => this.amount(cents, name));
  }

  amount(cents, name) {
    const [places, thousands, decimal] = BJ_MONEY_RULES[name] || BJ_MONEY_RULES.amount;
    const [whole, fraction] = (cents / 100).toFixed(places).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? grouped + decimal + fraction : grouped;
  }

  get selected() {
    return this.packages.find((input) => input.checked) || this.packages[0];
  }

  get subscribed() {
    return this.subToggle?.getAttribute('aria-checked') === 'true';
  }

  get quantity() {
    if (this.singleItem) return 1;
    return Math.max(1, parseInt(this.qtyInput?.value, 10) || 1);
  }

  /* Tymczasowy limit „jeden pakiet w koszyku" (settings.bj_single_item_cart).
     Sekcja renderuje wtedy ukryte quantity=1 zamiast licznika sztuk. */
  get singleItem() {
    return this.hasAttribute('data-single-item');
  }

  stepQty(direction) {
    if (!this.qtyInput) return;
    this.qtyInput.value = Math.max(1, this.quantity + direction);
    this.render();
  }

  toggleSubscription() {
    if (!this.subToggle) return;
    this.subToggle.setAttribute('aria-checked', String(!this.subscribed));
    this.render();
  }

  onChange() {
    this.render();
  }

  render() {
    const pkg = this.selected;
    if (!pkg) return;

    // Plan subskrypcji istnieje tylko dla wariantów, które go dostały w Shopify.
    const planId = pkg.dataset.planId;
    const useplan = this.subscribed && planId;
    const unit = useplan ? parseInt(pkg.dataset.planPrice, 10) : parseInt(pkg.dataset.price, 10);

    if (this.variantInput) this.variantInput.value = pkg.value;
    if (this.variantMirror) this.variantMirror.value = pkg.value;

    if (this.planInput) {
      this.planInput.value = useplan ? planId : '';
      // Puste selling_plan wysłane do /cart/add jest błędem — pole musi zniknąć.
      this.planInput.disabled = !useplan;
    }

    if (this.priceEl) this.priceEl.textContent = this.money(unit * this.quantity);

    if (this.compareEl) {
      // W subskrypcji przekreślamy cenę jednorazową — to jest realna oszczędność.
      const compare = useplan ? this.money(parseInt(pkg.dataset.price, 10) * this.quantity) : pkg.dataset.compareFormatted;
      this.compareEl.textContent = compare || '';
      this.compareEl.hidden = !compare;
    }
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  /* Adres kasy wyprowadzamy z routes.cart_url, a nie wpisujemy '/checkout'
     na sztywno — przy sklepie z prefiksem językowym ścieżka ma ten prefiks. */
  checkoutUrl() {
    const cart = window.routes?.cart_url || '/cart';
    return cart.endsWith('/cart') ? `${cart.slice(0, -5)}/checkout` : '/checkout';
  }

  async onSubmit(event) {
    const drawer = document.querySelector('cart-drawer');
    const useDrawer = !this.checkoutDirect && drawer && typeof drawer.renderContents === 'function';
    // Bez szuflady i bez trybu „prosto do kasy" zostawiamy natywny POST —
    // przeglądarka sama przejdzie na /cart.
    if (!useDrawer && !this.checkoutDirect) return;

    event.preventDefault();
    this.showError('');
    this.submit?.setAttribute('aria-disabled', 'true');

    let redirecting = false;
    const formData = new FormData(this.form);
    const variantId = formData.get('id');

    try {
      if (useDrawer) {
        formData.append('sections', drawer.getSectionsToRender().map((s) => s.id));
        formData.append('sections_url', window.location.pathname);
        drawer.setActiveElement(document.activeElement);
      }

      // Przy limicie koszyk czyścimy przed dodaniem, więc wybór innego pakietu
      // podmienia zawartość zamiast się do niej dokładać. Czyścimy przed, nie
      // po dodaniu, bo /cart/update.js adresuje pozycje po ID wariantu — a ten
      // sam wariant raz jednorazowo i raz w subskrypcji to dwie osobne pozycje
      // o tym samym ID. Sklep jest jednoproduktowy i ma wyłączone śledzenie
      // stanów, więc dodanie po wyczyszczeniu praktycznie nie ma jak się nie
      // udać; gdyby jednak padło, klient zobaczy błąd i wybierze pakiet ponownie.
      if (this.singleItem) {
        await fetch(`${window.routes.cart_url}/clear.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
      }

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();

      if (result.status) {
        this.showError(result.description || result.message);
        return;
      }

      // Prosto do kasy: przycisk zostaje wyłączony, bo za moment i tak
      // wychodzimy ze strony, a to blokuje dubla przy szybkim drugim kliknięciu.
      if (this.checkoutDirect) {
        redirecting = true;
        window.location.assign(this.checkoutUrl());
        return;
      }

      // Dawn i aplikacje (upsell, subskrypcje) nasłuchują tego zdarzenia,
      // żeby odświeżyć swoje fragmenty koszyka. Bez niego widzą pusty koszyk.
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bj-buy-box', productVariantId: variantId, cartData: result });
      }

      drawer.renderContents(result);
    } catch {
      // Sieć padła — nie zostawiamy klienta bez informacji zwrotnej.
      this.showError('Nie udało się dodać do koszyka. Spróbuj ponownie.');
    } finally {
      // renderContents zdejmuje is-empty tylko z .drawer__inner, a klasa siedzi
      // też na samym <cart-drawer> — Dawn kasuje ją w product-form.js. Bez tego
      // szuflada otwiera się bez nagłówka i bez listy pozycji.
      drawer?.classList.remove('is-empty');
      if (!redirecting) this.submit?.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('bj-buy-box')) {
  customElements.define('bj-buy-box', BjBuyBox);
}
